import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { shouldLoadAdSense, loadAdSense, removeAdSense } from '../utils/adsense';

const { FiClock, FiBookOpen, FiGrid, FiTrendingUp } = FiIcons;

const Layout = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Conditionally load AdSense based on current route
    if (shouldLoadAdSense(location.pathname)) {
      loadAdSense();
    } else {
      // Remove AdSense from pages that shouldn't have it
      removeAdSense();
    }
  }, [location.pathname]);
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Accent Bar */}
      <div className="h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500 w-full" />
      
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <nav className="flex flex-wrap items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2 text-2xl font-black tracking-tight text-slate-900 hover:text-blue-600 transition-all group">
              <div className="group-hover:rotate-12 transition-transform">
                <img src="/favicon.svg" alt="UK Salary Toolkit" className="w-8 h-8" />
              </div>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-600">
                UK Salary Toolkit
              </span>
            </Link>
            
            <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-sm font-semibold">
              <Link to="/salary-to-hourly" className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition-all">
                <SafeIcon icon={FiClock} className="text-blue-500" />
                <span className="hidden sm:inline">Hourly Rate</span>
              </Link>
              <Link to="/student-loan-estimator" className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-all">
                <SafeIcon icon={FiBookOpen} className="text-indigo-500" />
                <span className="hidden sm:inline">Student Loans</span>
              </Link>
              <Link to="/take-home-table" className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-all">
                <SafeIcon icon={FiGrid} className="text-emerald-500" />
                <span className="hidden sm:inline">Pay Tables</span>
              </Link>
              <Link to="/how-to-use" className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-slate-600 hover:text-amber-600 hover:bg-amber-50 transition-all">
                <SafeIcon icon={FiIcons.FiHelpCircle} className="text-amber-500" />
                <span className="hidden sm:inline">How to Use</span>
              </Link>
            </div>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {children}
        </motion.div>
      </main>

      <footer className="bg-slate-900 text-slate-300 border-t mt-16">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <SafeIcon icon={FiTrendingUp} className="text-blue-400" />
                UK Salary Toolkit
              </h3>
              <p className="text-sm leading-relaxed opacity-80">
                The most accurate 2025/26 UK tax calculator including National Insurance, 
                Student Loans, and Pension deductions. Designed for simplicity and speed.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Tools</h4>
              <ul className="text-sm space-y-2 opacity-80">
                <li><Link to="/" className="hover:text-blue-400 transition-colors">Tax Calculator</Link></li>
                <li><Link to="/salary-to-hourly" className="hover:text-blue-400 transition-colors">Hourly Rate Converter</Link></li>
                <li><Link to="/student-loan-estimator" className="hover:text-blue-400 transition-colors">Student Loan Estimator</Link></li>
                <li><Link to="/take-home-table" className="hover:text-blue-400 transition-colors">2025/26 Pay Tables</Link></li>
                <li><Link to="/how-to-use" className="hover:text-blue-400 transition-colors">How to Use</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Helpful Links</h4>
              <ul className="text-sm space-y-2 opacity-80">
                <li><a href="https://www.gov.uk/estimate-income-tax" target="_blank" rel="noreferrer" className="hover:text-blue-400 transition-colors">Check Your Tax (GOV.UK)</a></li>
                <li><a href="https://www.gov.uk/national-insurance-rates-letters" target="_blank" rel="noreferrer" className="hover:text-blue-400 transition-colors">NI Rates Guide</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800">
            <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
              <Link to="/about" className="text-slate-400 hover:text-blue-400 transition-colors">About</Link>
              <Link to="/contact" className="text-slate-400 hover:text-blue-400 transition-colors">Contact</Link>
              <Link to="/privacy-policy" className="text-slate-400 hover:text-blue-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="text-slate-400 hover:text-blue-400 transition-colors">Terms of Service</Link>
            </div>
            <div className="text-center text-xs opacity-60">
              <p>© 2026 UK Salary Toolkit. Tax calculations based on 2025/26 tax year. Not financial advice.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;